import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';

import { isLineOver } from './calculations';
import { editableMoney, editablePercent, parseNumber, percent } from './formatting';

import Button from 'CommonComponents/Button';

const MAX_PCT = 999.999999;
const WIDTH = 300;

const roundPct = (pct) => Math.round(pct * 1e6) / 1e6;

/**
 * What a write in this cell reaches: on a line, the line's budget; on a department,
 * cost centre or project, the budget of every line beneath except negative project
 * balances, whose stored values a write keeps (`base`).
 */
const writeScope = (row, cell, figures) =>
    row.kind === 'line' ? { base: 0, budget: figures.budget } : { base: cell.blockedValue || 0, budget: figures.writableBudget };

/**
 * The popover editor for one allocation cell. % and value stay in step; Enter saves,
 * Tab saves and moves to the next column, Esc cancels. An empty % clears the
 * allocation. On a department, cost-centre or project row the % is written to every line
 * beneath it (a project's balance included, unless below zero). The editor opens on the
 * cell's current value and the % that gives it; until either field is edited, Enter
 * cancels and Tab moves on without saving, so the lines beneath keep their own values.
 * A negative project balance holding a stored value opens in clear-only mode: it cannot
 * take a new allocation, but what it holds can be removed.
 *
 * `countOverAfter(pct)` (department, cost-centre and project rows) is how many lines
 * beneath would be over 100% once `pct` is written.
 */
export default function CellEditor({ anchor, row, column, figures, saving, onSave, onCancel, onSkip, countOverAfter }) {
    const cell = figures.columns[column.id];
    const budget = figures.budget;
    const scope = writeScope(row, cell, figures);
    const valueFor = (pctValue) => scope.base + (scope.budget * pctValue) / 100;
    const pctFor = (value) => roundPct(((value - scope.base) / scope.budget) * 100);
    const initialPct = !cell.any ? null : row.kind === 'line' ? cell.pct : scope.budget > 0 ? pctFor(cell.value) : null;

    const [pctText, setPctText] = useState(editablePercent(initialPct));
    const [valueText, setValueText] = useState(!cell.any || scope.budget <= 0 ? '' : editableMoney(cell.value));
    const [touched, setTouched] = useState(false);
    const [error, setError] = useState(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    const panelRef = useRef(null);
    const pctRef = useRef(null);
    const clearOnly = row.kind === 'line' && figures.blocked;

    useLayoutEffect(() => {
        const place = () => {
            const rect = anchor.getBoundingClientRect();
            const left = Math.max(window.scrollX + 8, rect.right + window.scrollX - WIDTH);
            setPosition({ top: rect.bottom + window.scrollY + 6, left });
        };

        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);

        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [anchor]);

    useEffect(() => {
        pctRef.current?.focus();
        pctRef.current?.select();
    }, []);

    useEffect(() => {
        const onMouseDown = (event) => {
            if (!panelRef.current?.contains(event.target) && !anchor.contains(event.target)) {
                onCancel();
            }
        };

        document.addEventListener('mousedown', onMouseDown);

        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [anchor, onCancel]);

    const pct = parseNumber(pctText);
    const typedPct = pct === null || Number.isNaN(pct) ? 0 : pct;
    // A line shows its total after save; a parent counts the lines beneath that would be over.
    const totalAfter = row.kind === 'line' ? (figures.totalPct ?? 0) - (cell.any ? cell.pct : 0) + typedPct : null;
    const linesOverAfter = row.kind === 'line' ? 0 : countOverAfter(pct === null || Number.isNaN(pct) ? null : pct);
    const overAfter = row.kind === 'line' ? isLineOver(budget, totalAfter) : linesOverAfter > 0;

    const onPctChange = (text) => {
        setPctText(text);
        setTouched(true);
        setError(null);
        const next = parseNumber(text);
        setValueText(next === null || Number.isNaN(next) || scope.budget <= 0 ? '' : editableMoney(valueFor(next)));
    };

    const onValueChange = (text) => {
        setValueText(text);
        setTouched(true);
        setError(null);
        const next = parseNumber(text);
        setPctText(next === null || Number.isNaN(next) ? '' : editablePercent(pctFor(next)));
    };

    const save = (moveNext = false) => {
        if (row.kind !== 'line' && !touched) {
            moveNext ? onSkip() : onCancel();
            return;
        }

        if (pct !== null && (Number.isNaN(pct) || pct < 0 || pct > MAX_PCT)) {
            setError($t('Enter a percentage between 0 and 999.999999.'));
            return;
        }

        onSave(pct, moveNext);
    };

    const onKeyDown = (event) => {
        if (clearOnly) {
            // Only Remove (Enter on the focused button) and Esc act here.
            if (event.key === 'Escape') {
                event.preventDefault();
                onCancel();
            }

            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
        } else if (event.key === 'Enter') {
            event.preventDefault();
            save(false);
        } else if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            save(true);
        }
    };

    const one = figures.writableCount === 1;
    const scopeHints = {
        department: one
            ? $t('Applies to the 1 line in this department')
            : $t('Applies to all {{count}} lines in this department', { count: figures.writableCount }),
        costcenter: one
            ? $t('Applies to the 1 line in this cost centre')
            : $t('Applies to all {{count}} lines in this cost centre', { count: figures.writableCount }),
        project: one ? $t('Applies to the 1 line in this project') : $t('Applies to all {{count}} lines in this project', { count: figures.writableCount }),
    };
    const scopeHint = scopeHints[row.kind] ?? $t('Enter % or value');

    const afterSave = () => {
        if (row.kind === 'line') {
            return overAfter
                ? $t('Line total after save: {{pct}} (over 100%)', { pct: percent(totalAfter) })
                : $t('Line total after save: {{pct}}', { pct: percent(totalAfter) });
        }

        if (linesOverAfter === 0) {
            return null;
        }

        return linesOverAfter === 1 ? $t('1 line over 100% after save') : $t('{{count}} lines over 100% after save', { count: linesOverAfter });
    };

    const inputClass = 'text-input tw-w-full tw-text-end disabled:tw-cursor-not-allowed disabled:tw-opacity-50';

    return createPortal(
        <div
            ref={panelRef}
            role="dialog"
            aria-label={$t('Edit allocation')}
            onKeyDown={onKeyDown}
            style={{ top: position.top, left: position.left, width: WIDTH }}
            className="tw-absolute tw-z-60 tw-rounded-lg tw-border tw-border-grey tw-bg-white tw-p-3 tw-text-start tw-shadow-primary"
        >
            <div className="tw-mb-2 tw-text-xs tw-text-charcoal-50">
                <b className="tw-font-medium tw-text-charcoal">{column.name}</b> · {row.path}
            </div>
            {clearOnly ? (
                <>
                    <p className="tw-m-0 tw-text-2xs tw-text-charcoal-75">
                        {$t('A project balance below zero cannot be allocated. The value stored here can be removed.')}
                    </p>
                    <div className="tw-mt-2.5 tw-flex tw-justify-end tw-gap-x-1.5">
                        <Button variant="secondary" small onClick={onCancel} disabled={saving}>
                            {$t('Cancel')}
                        </Button>
                        <Button autoFocus variant="danger" small onClick={() => onSave(null, false)} disabled={saving} loading={saving}>
                            {$t('Remove allocation')}
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    <div className="tw-flex tw-gap-x-2">
                        <label className="tw-flex tw-flex-1 tw-flex-col tw-gap-y-1 tw-text-2xs tw-text-charcoal-50">
                            {$t('% of line')}
                            <input
                                ref={pctRef}
                                className={inputClass}
                                inputMode="decimal"
                                value={pctText}
                                placeholder="0"
                                onChange={(e) => onPctChange(e.target.value)}
                            />
                        </label>
                        <label className="tw-flex tw-flex-1 tw-flex-col tw-gap-y-1 tw-text-2xs tw-text-charcoal-50">
                            {$t('Value')}
                            <input
                                className={inputClass}
                                inputMode="decimal"
                                value={valueText}
                                placeholder={scope.budget > 0 ? '0' : '–'}
                                disabled={scope.budget <= 0}
                                title={scope.budget > 0 ? undefined : $t('This line has no budget, so only a percentage can be entered.')}
                                onChange={(e) => onValueChange(e.target.value)}
                            />
                        </label>
                    </div>
                    <div
                        className={classNames(
                            'tw-mt-2 tw-flex tw-justify-between tw-gap-x-2 tw-text-2xs',
                            overAfter || error ? 'tw-text-red' : 'tw-text-charcoal-50',
                        )}
                    >
                        <span>{error || scopeHint}</span>
                        {!error && afterSave() && <span className="tw-text-end">{afterSave()}</span>}
                    </div>
                    <div className="tw-mt-2.5 tw-flex tw-justify-end tw-gap-x-1.5">
                        <Button variant="secondary" small onClick={onCancel} disabled={saving}>
                            {$t('Cancel')}
                        </Button>
                        <Button variant="primary" small onClick={() => save(false)} disabled={saving} loading={saving}>
                            {$t('Save')}
                        </Button>
                    </div>
                </>
            )}
        </div>,
        document.body,
    );
}
